import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { toast } from "react-toastify";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { leadIntegrationApi } from "../../api/leadIntegrationApi";
import { BASE_URL } from "../../config/apiconfig";

/**
 * Settings → Integrations panel for lead providers (IndiaMART / TradeIndia).
 * Each provider has its own configuration row in lead_provider_settings; this
 * component owns Save / Verify / Sync so it never touches the TallyPrime
 * accounting-settings state next to it.
 */

const STATUS_META = {
  NOT_CONFIGURED: { label: "Not Configured", color: "#64748B", bg: "#F1F5F9" },
  DISCONNECTED: { label: "Disconnected", color: "#64748B", bg: "#F1F5F9" },
  CONNECTED: { label: "Connected", color: "#16A34A", bg: "#DCFCE7" },
  INVALID_CREDENTIALS: { label: "Invalid Credentials", color: "#DC2626", bg: "#FEE2E2" },
  API_ERROR: { label: "API Error", color: "#EA580C", bg: "#FFEDD5" },
};

const INTERVAL_OPTIONS = [5, 10, 15, 30, 60];

const sectionTitleSx = {
  fontWeight: 600,
  color: "var(--settings-primary)",
  mb: 2,
  fontSize: "0.875rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const innerBoxSx = {
  p: 2.5,
  backgroundColor: "var(--settings-inner-bg)",
  borderRadius: 2,
  border: "1px solid var(--settings-border)",
};

const fieldLabelSx = {
  fontWeight: 500,
  color: "var(--settings-text)",
  mb: 1,
  fontSize: "0.875rem",
};

const fmtDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const errMsg = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  e?.message ||
  "Request failed";

const originFromBase = () => String(BASE_URL || "").replace(/\/api\/v1\/?$/, "");

const LeadProviderPanel = ({ provider }) => {
  const isIndiaMart = provider === "IndiaMART";

  const [config, setConfig] = useState({
    accountIdentifier: "",
    secondaryIdentifier: "",
    apiKey: "",
    endpointUrl: "",
    autoSync: false,
    syncIntervalMinutes: 15,
  });
  const [status, setStatus] = useState({
    status: "NOT_CONFIGURED",
    verificationMessage: "",
    lastVerifiedAt: null,
    lastSyncAt: null,
    lastSyncSummary: null,
    configured: false,
    pendingFailures: 0,
    webhookPath: null,
    pullApiEndpoint: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const applySettings = useCallback((data) => {
    setConfig({
      accountIdentifier: data.accountIdentifier || "",
      secondaryIdentifier: data.secondaryIdentifier || "",
      apiKey: data.apiKey || "",
      endpointUrl: data.endpointUrl || "",
      autoSync: !!data.autoSync,
      syncIntervalMinutes: data.syncIntervalMinutes || 15,
    });
    setStatus({
      status: data.status || "NOT_CONFIGURED",
      verificationMessage: data.verificationMessage || "",
      lastVerifiedAt: data.lastVerifiedAt || null,
      lastSyncAt: data.lastSyncAt || null,
      lastSyncSummary: data.lastSyncSummary || null,
      configured: !!data.configured,
      pendingFailures: data.pendingFailures || 0,
      webhookPath: data.webhookPath || null,
      pullApiEndpoint: data.pullApiEndpoint || null,
    });
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await leadIntegrationApi.getSyncLogs(provider, { page: 0, size: 10 });
      setLogs(res.data?.content || []);
    } catch {
      setLogs([]);
    }
  }, [provider]);

  const load = useCallback(async () => {
    setLoading(true);
    setVerifyResult(null);
    setSyncResult(null);
    try {
      const [settingsRes] = await Promise.all([
        leadIntegrationApi.getSettings(provider),
        loadLogs(),
      ]);
      applySettings(settingsRes.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [provider, applySettings, loadLogs]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      const settingsRes = await leadIntegrationApi.getSettings(provider);
      applySettings(settingsRes.data);
    } catch {
      // settings refresh is best effort
    }
    await loadLogs();
  }, [provider, applySettings, loadLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await leadIntegrationApi.saveSettings(provider, config);
      applySettings(res.data);
      toast.success("Configuration saved.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await leadIntegrationApi.verify(provider, config);
      setVerifyResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message || "Verification succeeded.");
      } else {
        toast.error(res.data.message || "Verification failed.");
      }
    } catch (e) {
      setVerifyResult({ success: false, status: "API_ERROR", message: errMsg(e), verificationResponse: e?.response?.data?.message || null });
      toast.error(errMsg(e));
    } finally {
      setVerifying(false);
      try {
        const settingsRes = await leadIntegrationApi.getSettings(provider);
        applySettings(settingsRes.data);
      } catch {
        // settings refresh is best effort
      }
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await leadIntegrationApi.sync(provider);
      setSyncResult(res.data);
      toast.success(res.data.message || "Sync completed.");
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
      await refresh();
    } finally {
      setSyncing(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await leadIntegrationApi.retryFailed(provider);
      setSyncResult(res.data);
      toast.success(res.data.message || "Retry completed.");
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  const updateField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const webhookUrl = status.webhookPath ? originFromBase() + status.webhookPath : null;
  const busy = saving || verifying || syncing || retrying;

  const statusMeta = STATUS_META[status.status] || STATUS_META.NOT_CONFIGURED;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "var(--settings-primary)" }} />
      </Box>
    );
  }

  return (
    <Box data-testid="lead-provider-panel">
      {/* Integration Status */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Integration Status
        </Typography>
        <Box sx={innerBoxSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
            <Chip
              label={verifying ? "Verifying" : statusMeta.label}
              size="small"
              sx={{
                backgroundColor: verifying ? "#DBEAFE" : statusMeta.bg,
                color: verifying ? "#1D4ED8" : statusMeta.color,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {status.configured && (
              <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.75rem" }}>
                Credentials saved
              </Typography>
            )}
            {status.pendingFailures > 0 && (
              <Chip
                label={`${status.pendingFailures} failed record(s) pending retry`}
                size="small"
                sx={{ backgroundColor: "#FEE2E2", color: "#DC2626", fontWeight: 600, fontSize: "0.72rem" }}
              />
            )}
          </Box>
          {status.verificationMessage && (
            <Typography variant="body2" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.8125rem", mb: 1 }}>
              {status.verificationMessage}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem" }}>
                Last verified
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--settings-text)", fontSize: "0.8125rem" }}>
                {fmtDate(status.lastVerifiedAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem" }}>
                Last sync
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--settings-text)", fontSize: "0.8125rem" }}>
                {fmtDate(status.lastSyncAt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Provider Configuration */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          {provider} Configuration
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={isIndiaMart ? 4 : 4}>
            <Box sx={innerBoxSx}>
              <Typography variant="body2" sx={fieldLabelSx}>
                {isIndiaMart ? "Registered Seller Mobile Number *" : "User ID *"}
              </Typography>
              <TextField
                size="small"
                value={config.accountIdentifier}
                onChange={updateField("accountIdentifier")}
                fullWidth
                placeholder={isIndiaMart ? "Enter registered mobile number" : "Enter TradeIndia user ID"}
              />
            </Box>
          </Grid>
          {!isIndiaMart && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={innerBoxSx}>
                <Typography variant="body2" sx={fieldLabelSx}>
                  Profile ID *
                </Typography>
                <TextField
                  size="small"
                  value={config.secondaryIdentifier}
                  onChange={updateField("secondaryIdentifier")}
                  fullWidth
                  placeholder="Enter profile ID"
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={isIndiaMart ? 4 : 4}>
            <Box sx={innerBoxSx}>
              <Typography variant="body2" sx={fieldLabelSx}>
                {isIndiaMart ? "CRM API Key *" : "API Key *"}
              </Typography>
              <TextField
                size="small"
                type="password"
                value={config.apiKey}
                onChange={updateField("apiKey")}
                fullWidth
                placeholder={config.apiKey ? "" : "Enter API key"}
                autoComplete="off"
              />
            </Box>
          </Grid>
          {!isIndiaMart && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={innerBoxSx}>
                <Typography variant="body2" sx={fieldLabelSx}>
                  API Link (optional)
                </Typography>
                <TextField
                  size="small"
                  value={config.endpointUrl}
                  onChange={updateField("endpointUrl")}
                  fullWidth
                  placeholder="https://www.tradeindia.com/utils/my_inquiry.html"
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={isIndiaMart ? 4 : 4}>
            <Box sx={innerBoxSx}>
              <Typography variant="body2" sx={fieldLabelSx}>
                Automatic Lead Sync
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.autoSync}
                      onChange={updateField("autoSync")}
                      size="small"
                      inputProps={{ "aria-label": "Automatic lead sync" }}
                    />
                  }
                  label={config.autoSync ? "On" : "Off"}
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem", color: "var(--settings-text)" } }}
                />
                <Select
                  size="small"
                  value={config.syncIntervalMinutes}
                  onChange={updateField("syncIntervalMinutes")}
                  disabled={!config.autoSync}
                  sx={{
                    minWidth: 110,
                    backgroundColor: "var(--settings-card-bg)",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--settings-border-hover)" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94A3B8" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--settings-primary)",
                      borderWidth: "2px",
                    },
                  }}
                >
                  {INTERVAL_OPTIONS.map((min) => (
                    <MenuItem key={min} value={min}>
                      Every {min} min
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={innerBoxSx}>
              <Typography variant="body2" sx={fieldLabelSx}>
                Pull API Endpoint
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "var(--settings-text-secondary)",
                  fontSize: "0.75rem",
                  wordBreak: "break-all",
                  mt: 0.5,
                }}
              >
                {status.pullApiEndpoint || "—"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {isIndiaMart && webhookUrl && (
          <Box sx={{ ...innerBoxSx, mt: 2 }}>
            <Typography variant="body2" sx={fieldLabelSx}>
              IndiaMART Push Webhook URL
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "var(--settings-text-secondary)", fontSize: "0.75rem", wordBreak: "break-all", flex: 1 }}
              >
                {webhookUrl}
              </Typography>
              <Button
                size="small"
                startIcon={<CopyIcon sx={{ fontSize: "0.9rem !important" }} />}
                onClick={() => {
                  navigator.clipboard?.writeText(webhookUrl);
                  toast.info("Webhook URL copied.");
                }}
                sx={{ color: "var(--settings-primary)", textTransform: "none", fontSize: "0.75rem", whiteSpace: "nowrap" }}
              >
                Copy
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem" }}>
              Paste this URL in your IndiaMART account (LMS → API Integration → Push) to receive leads instantly.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={busy}
            sx={{
              borderRadius: 2,
              borderColor: "var(--settings-primary)",
              color: "var(--settings-primary)",
              fontSize: "0.8125rem",
              px: 2.5,
              py: 1,
              "&:hover": { borderColor: "var(--settings-primary)", backgroundColor: "rgba(79,70,229,0.04)" },
            }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: "var(--settings-primary)" }} /> : "Save Configuration"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleVerify}
            disabled={busy}
            sx={{
              borderRadius: 2,
              borderColor: "var(--settings-border-hover)",
              color: "var(--settings-text)",
              fontSize: "0.8125rem",
              px: 2.5,
              py: 1,
              "&:hover": { borderColor: "var(--settings-primary)", backgroundColor: "var(--settings-inner-bg)" },
            }}
          >
            {verifying ? <CircularProgress size={16} sx={{ color: "var(--settings-primary)" }} /> : "Verify Account"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={busy || !status.configured}
            sx={{
              borderRadius: 2,
              backgroundColor: "var(--settings-primary)",
              color: "#fff",
              fontSize: "0.8125rem",
              px: 2.5,
              py: 1,
              boxShadow: "none",
              "&:hover": { backgroundColor: "var(--settings-primary-hover)", boxShadow: "none" },
            }}
          >
            {syncing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Sync Leads Now"}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Verification Result */}
      {verifyResult && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Verification Result
          </Typography>
          <Box
            sx={{
              ...innerBoxSx,
              borderLeft: `4px solid ${verifyResult.success ? "#16A34A" : "#DC2626"}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Chip
                label={verifyResult.success ? "Success" : "Failed"}
                size="small"
                sx={{
                  backgroundColor: verifyResult.success ? "#DCFCE7" : "#FEE2E2",
                  color: verifyResult.success ? "#16A34A" : "#DC2626",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                }}
              />
              {verifyResult.status && (
                <Chip
                  label={(STATUS_META[verifyResult.status]?.label || verifyResult.status).toUpperCase()}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              )}
            </Box>
            {verifyResult.message && (
              <Typography variant="body2" sx={{ color: "var(--settings-text)", fontSize: "0.8125rem", mb: verifyResult.verificationResponse ? 1 : 0 }}>
                {verifyResult.message}
              </Typography>
            )}
            {verifyResult.verificationResponse && (
              <Typography
                variant="body2"
                sx={{
                  color: "var(--settings-text-secondary)",
                  fontSize: "0.75rem",
                  fontFamily: "monospace",
                  backgroundColor: "var(--settings-card-bg)",
                  borderRadius: 1,
                  p: 1.25,
                  wordBreak: "break-all",
                }}
              >
                Actual response: {verifyResult.verificationResponse}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem", display: "block", mt: 1 }}>
              Verified at: {fmtDate(verifyResult.verifiedAt)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Synchronization Result */}
      {(syncResult || status.lastSyncSummary) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Synchronization Result
          </Typography>
          <Box sx={innerBoxSx}>
            {(() => {
              const data = syncResult || status.lastSyncSummary || {};
              const cells = [
                { label: "Leads Imported", value: data.leadsImported ?? 0, color: "#16A34A" },
                { label: "Leads Updated", value: data.leadsUpdated ?? 0, color: "#2563EB" },
                { label: "Duplicates Skipped", value: data.duplicatesSkipped ?? 0, color: "#64748B" },
                { label: "Failed Records", value: data.failedRecords ?? 0, color: (data.failedRecords ?? 0) > 0 ? "#DC2626" : "#64748B" },
              ];
              return (
                <>
                  <Grid container spacing={2}>
                    {cells.map((cell) => (
                      <Grid item xs={6} sm={3} key={cell.label}>
                        <Box sx={{ textAlign: "center", py: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: cell.color, fontSize: "1.25rem" }}>
                            {cell.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem" }}>
                            {cell.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="body2" sx={{ color: "var(--settings-text)", fontSize: "0.8125rem", mt: 1.5 }}>
                    {data.message}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.72rem", display: "block", mt: 0.5 }}>
                    Last sync: {fmtDate(data.lastSyncAt || status.lastSyncAt)}
                  </Typography>
                  {(status.pendingFailures > 0 || (syncResult && (syncResult.failedRecords ?? 0) > 0)) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleRetry}
                      disabled={busy}
                      sx={{
                        mt: 1.5,
                        borderRadius: 2,
                        borderColor: "#DC2626",
                        color: "#DC2626",
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.5,
                        "&:hover": { borderColor: "#DC2626", backgroundColor: "#FEF2F2" },
                      }}
                    >
                      {retrying ? <CircularProgress size={14} sx={{ color: "#DC2626" }} /> : "Retry Failed Records"}
                    </Button>
                  )}
                </>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* Sync History */}
      <Box>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Synchronization History
        </Typography>
        {logs.length === 0 ? (
          <Box sx={innerBoxSx}>
            <Typography variant="body2" sx={{ color: "var(--settings-text-secondary)", fontSize: "0.8125rem" }}>
              No synchronizations yet. Save your credentials, verify the account, then run Sync Leads Now.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ ...innerBoxSx, p: 0, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Time", "Trigger", "Status", "Imported", "Updated", "Duplicates", "Failed", "Message"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 600,
                        color: "var(--settings-text-secondary)",
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                      {fmtDate(log.startedAt)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{log.triggerType}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.status}
                        size="small"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          backgroundColor:
                            log.status === "SUCCESS" ? "#DCFCE7" : log.status === "FAILED" ? "#FEE2E2" : "#FFEDD5",
                          color: log.status === "SUCCESS" ? "#16A34A" : log.status === "FAILED" ? "#DC2626" : "#EA580C",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{log.leadsImported ?? 0}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{log.leadsUpdated ?? 0}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{log.duplicatesSkipped ?? 0}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{log.failedRecords ?? 0}</TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.72rem",
                        color: "var(--settings-text-secondary)",
                        maxWidth: 260,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.message || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LeadProviderPanel;
